// Helpers para parsing/formato de importes y monedas reusables fuera del flujo de cart.
// Extraídos de CartClient cuando el campo "Importe recibido" se movió al final del form
// presencial.

// Mapeo `lang` (prefijo BCP-47 de la ruta) → locale completo para `Intl`.
export const LANG_TO_LOCALE: Record<string, string> = {
  es: 'es-ES', en: 'en-US', pt: 'pt-PT', ca: 'ca-ES', eu: 'eu-ES', fr: 'fr-FR', it: 'it-IT',
};

// Devuelve true si los dos importes coinciden con tolerancia de medio céntimo (evita falsos
// negativos por artefactos de coma flotante al parsear). Mismo umbral que `formatPrice`.
export function amountsEqual(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.005;
}

// `event.currencyName` a veces llega como símbolo ('€', '$') en lugar del código ISO 4217
// que exige Intl.NumberFormat. Mapa compartido — antes vivía duplicado como SYMBOL_TO_ISO
// en CartClient/RegistrationSidebar/WaitlistPaymentClient.
const SYMBOL_TO_ISO: Record<string, string> = { '€': 'EUR', '$': 'USD', '£': 'GBP', '¥': 'JPY' };

export function normalizeCurrencyCode(currency?: string | null): string {
  const raw = (currency ?? '').trim();
  if (!raw) return 'EUR';
  return SYMBOL_TO_ISO[raw] ?? raw.toUpperCase();
}

// Monedas que se pintan con la convención de su país en lugar de la del locale del browser.
// CLP: en Chile el símbolo va delante y sin decimales ("$2.000"); con el locale español del
// browser Intl lo pintaría "2.000 $" (y con decimales si se fuerzan).
const CURRENCY_LOCALE_OVERRIDES: Record<string, string> = { CLP: 'es-CL' };

// Devuelve solo el símbolo de la moneda en el locale dado (€, $, £, etc.) usando
// `Intl.NumberFormat.formatToParts`. Útil como adorno de inputs de importe.
export function getCurrencySymbol(currency: string, lang: string): string {
  const code = normalizeCurrencyCode(currency);
  try {
    const parts = new Intl.NumberFormat(CURRENCY_LOCALE_OVERRIDES[code] ?? lang, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? code;
  } catch {
    return code;
  }
}

// Formatea un importe con su moneda (símbolo estrecho). Los decimales los decide Intl según
// la moneda (EUR → 2, CLP → 0), NO se fuerzan a 2. El locale es el del browser salvo override
// por moneda (ver CURRENCY_LOCALE_OVERRIDES); `lang` opcional para SSR/consistencia por página.
// Cualquier valor con |x| < 0,005 se normaliza a 0 para evitar "-0,00 €" por artefactos de
// coma flotante. Este es el helper canónico — no crear formatPrice locales nuevos.
export function formatPrice(value: number, currency?: string, lang?: string): string {
  const code = normalizeCurrencyCode(currency);
  const safe = Math.abs(value) < 0.005 ? 0 : value;
  try {
    return new Intl.NumberFormat(CURRENCY_LOCALE_OVERRIDES[code] ?? lang, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).format(safe);
  } catch {
    // Código de moneda no reconocido por Intl (dato libre en Cosmos) — degradar legible.
    return `${safe.toFixed(2)} ${code}`;
  }
}

// Parsea un string introducido por el usuario aceptando coma O punto como separador decimal.
// Si el string contiene varios separadores (p.ej. "1.234,56"), interpreta el último como
// decimal y descarta los demás (miles).
export function parseLocaleAmount(s: string): number {
  const lastSep = Math.max(s.lastIndexOf('.'), s.lastIndexOf(','));
  if (lastSep < 0) {
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  }
  const intPart = s.slice(0, lastSep).replace(/[.,]/g, '');
  const decPart = s.slice(lastSep + 1);
  const n = parseFloat(`${intPart}.${decPart}`);
  return Number.isFinite(n) ? n : 0;
}
